import { useState, useEffect, useCallback, useMemo } from 'react';

// Site mood types - the site decides its own emotional state
export type SiteMood = 'calm' | 'detached' | 'curious' | 'unsettled';

interface MoodState {
  currentMood: SiteMood;
  moodIntensity: number; // 0-1
  lastMoodChange: number;
  moodHistory: SiteMood[];
}

interface MoodEffects {
  textTone: 'warm' | 'cold' | 'questioning' | 'distant';
  animationSpeed: 'slow' | 'normal' | 'erratic';
  colorShift: number; // -1 to 1
  roomBias: string[]; // room categories more likely to appear
}

const STORAGE_KEY = 'human-rooms-site-mood';

const getDefaultMoodState = (): MoodState => ({
  currentMood: 'calm',
  moodIntensity: 0.5,
  lastMoodChange: Date.now(),
  moodHistory: ['calm'],
});

const loadMoodState = (): MoodState => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...getDefaultMoodState(), ...JSON.parse(stored) };
    }
  } catch {}
  return getDefaultMoodState();
};

const saveMoodState = (state: MoodState) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

// The site decides its mood based on various factors
const calculateMood = (
  _currentState: MoodState,
  userBehavior: { rushScore: number; patience: number; visits: number }
): { mood: SiteMood; intensity: number } => {
  const hour = new Date().getHours();
  const isNight = hour >= 23 || hour < 5;
  const isEarly = hour >= 5 && hour < 8;
  const isLate = hour >= 20 && hour < 23;
  
  // Time-based influences
  let moodWeight = {
    calm: 25,
    detached: 25,
    curious: 25,
    unsettled: 25,
  };

  // Night makes the site more unsettled or detached
  if (isNight) {
    moodWeight.unsettled += 30;
    moodWeight.detached += 20;
    moodWeight.calm -= 25;
  }

  // Early morning - curious and calm
  if (isEarly) {
    moodWeight.curious += 25;
    moodWeight.calm += 20;
    moodWeight.unsettled -= 20;
  }

  // Late evening - detached, contemplative
  if (isLate) {
    moodWeight.detached += 25;
    moodWeight.curious += 15;
  }

  // User behavior influences
  if (userBehavior.rushScore > 60) {
    moodWeight.detached += 20;
    moodWeight.calm -= 15;
  }

  if (userBehavior.patience > 70) {
    moodWeight.calm += 20;
    moodWeight.curious += 15;
  }

  // Recent visitors make it more curious
  if (userBehavior.visits > 20) {
    moodWeight.curious += 15;
    moodWeight.detached += 10;
  }

  // Random element - site has free will
  const moods: SiteMood[] = ['calm', 'detached', 'curious', 'unsettled'];
  
  // Weight the random selection
  const totalWeight = Object.values(moodWeight).reduce((a, b) => a + b, 0);
  let random = Math.random() * totalWeight;
  
  let selectedMood: SiteMood = 'calm';
  for (const mood of moods) {
    random -= moodWeight[mood];
    if (random <= 0) {
      selectedMood = mood;
      break;
    }
  }

  // Intensity based on time and behavior
  let intensity = 0.5 + (isNight ? 0.2 : 0) + (userBehavior.patience > 70 ? 0.15 : 0);
  intensity = Math.min(1, Math.max(0, intensity + (Math.random() * 0.2 - 0.1)));

  return { mood: selectedMood, intensity };
};

export const useSiteMood = () => {
  const [moodState, setMoodState] = useState<MoodState>(loadMoodState);

  // Get user behavior from existing behavior system
  const getUserBehavior = useCallback(() => {
    try {
      const stored = localStorage.getItem('human-rooms-site-behavior');
      if (stored) {
        const behavior = JSON.parse(stored);
        return {
          rushScore: behavior.rushBehavior || 50,
          patience: behavior.patientBehavior || 50,
          visits: behavior.visitsCount || 0,
        };
      }
    } catch {}
    return { rushScore: 50, patience: 50, visits: 0 };
  }, []);

  // Periodically update mood (the site decides)
  useEffect(() => {
    // Initial mood calculation
    const userBehavior = getUserBehavior();
    const { mood, intensity } = calculateMood(moodState, userBehavior);
    
    if (mood !== moodState.currentMood || Math.abs(intensity - moodState.moodIntensity) > 0.2) {
      const updated = {
        ...moodState,
        currentMood: mood,
        moodIntensity: intensity,
        lastMoodChange: Date.now(),
        moodHistory: [...moodState.moodHistory.slice(-10), mood],
      };
      setMoodState(updated);
      saveMoodState(updated);
    }

    // Mood can shift every 2-5 minutes
    const interval = setInterval(() => {
      const userBehavior = getUserBehavior();
      const { mood, intensity } = calculateMood(moodState, userBehavior);
      
      // 20% chance to actually change
      if (Math.random() < 0.2 && mood !== moodState.currentMood) {
        setMoodState(prev => {
          const updated = {
            ...prev,
            currentMood: mood,
            moodIntensity: intensity,
            lastMoodChange: Date.now(),
            moodHistory: [...prev.moodHistory.slice(-10), mood],
          };
          saveMoodState(updated);
          return updated;
        });
      }
    }, 120000 + Math.random() * 180000);

    return () => clearInterval(interval);
  }, [getUserBehavior]);

  // Get mood effects for UI
  const moodEffects = useMemo((): MoodEffects => {
    const { currentMood, moodIntensity } = moodState;
    
    switch (currentMood) {
      case 'calm':
        return {
          textTone: 'warm',
          animationSpeed: 'slow',
          colorShift: 0.1 * moodIntensity,
          roomBias: ['truth', 'time', 'meta'],
        };
      case 'detached':
        return {
          textTone: 'cold',
          animationSpeed: 'slow',
          colorShift: -0.2 * moodIntensity,
          roomBias: ['nothing', 'identity', 'experimental'],
        };
      case 'curious':
        return {
          textTone: 'questioning',
          animationSpeed: 'normal',
          colorShift: 0,
          roomBias: ['games', 'experimental', 'visual'],
        };
      case 'unsettled':
        return {
          textTone: 'distant',
          animationSpeed: 'erratic',
          colorShift: -0.3 * moodIntensity,
          roomBias: ['scary', 'night', 'mortality'],
        };
      default:
        return {
          textTone: 'warm',
          animationSpeed: 'normal',
          colorShift: 0,
          roomBias: [],
        };
    }
  }, [moodState]);

  // Get mood-influenced messages
  const getMoodMessage = useCallback((): string | null => {
    const { currentMood, moodIntensity } = moodState;
    
    if (moodIntensity < 0.4) return null;
    
    const messages: Record<SiteMood, string[]> = {
      calm: [
        'The rooms wait patiently.',
        'Stillness permeates.',
        'Everything is in its place.',
      ],
      detached: [
        'Distance feels appropriate.',
        'Some questions have no answers.',
        'The site observes.',
      ],
      curious: [
        'What brought you here?',
        'There is more to discover.',
        'The rooms wonder about you.',
      ],
      unsettled: [
        'Something stirs.',
        'The shadows are restless.',
        'Not everything is as it seems.',
      ],
    };
    
    const moodMessages = messages[currentMood];
    return moodMessages[Math.floor(Math.random() * moodMessages.length)];
  }, [moodState]);

  // Check if a room should be highlighted based on mood
  const shouldHighlightRoom = useCallback((category: string): boolean => {
    return moodEffects.roomBias.includes(category) && Math.random() < 0.3;
  }, [moodEffects]);

  return {
    mood: moodState.currentMood,
    intensity: moodState.moodIntensity,
    effects: moodEffects,
    getMoodMessage,
    shouldHighlightRoom,
  };
};

export default useSiteMood;